import { catalogMonthlyUnit, catalogPriceTypeFromLabel } from './licenseCatalogMatch';

/** Alta de oportunidad en el portal de socios de Google (Partner Advantage). */

export const GOOGLE_PARTNER_PORTAL_URL = 'https://partners.cloud.google.com/u/0/opportunities/new';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const BILLING_MONTHS = { Mensual: 1, Trimestral: 3, Semestral: 6, Anual: 12 };

const trim = (value) => String(value ?? '').trim();

const roundMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100) / 100;
};

function amountsClose(left, right) {
  const a = Number(left);
  const b = Number(right);
  if (!(a > 0) || !(b > 0)) return false;
  return Math.abs(a - b) < 0.5;
}

/** 58,800 y 52,920: el segundo es el primero menos un porcentaje cerrado. */
function isOnlyClientDiscount(listAmount, netAmount) {
  const list = Number(listAmount);
  const net = Number(netAmount);
  if (!(list > net) || !(net > 0)) return false;
  const whole = Math.round((1 - net / list) * 100);
  if (whole <= 0 || whole >= 100) return false;
  if (Math.abs((1 - net / list) * 100 - whole) > 0.05) return false;
  return amountsClose(net, list * (1 - whole / 100));
}

/** Monto que ella registró. Si Nexus lo sustituyó por el total con descuento, se conserva el de lista. */
export function registeredOpportunityAmount(slot = {}) {
  const quoted = roundMoney(slot?.quotedAnnual);
  const amount = roundMoney(slot?.amount);
  if (quoted > 0 && amount > 0 && isOnlyClientDiscount(quoted, amount)) return quoted;
  return amount || quoted;
}

/** 403200 → "403,200.00" (es-MX: coma de miles, punto decimal). */
export function formatOpportunityAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  return n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Acepta "403,200.00" o "403200" y regresa el número. */
export function parseOpportunityAmount(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return '';
  const cleaned = raw.replace(/[^\d.,]/g, '');
  if (!cleaned) return '';
  const n = Number(cleaned.replace(/,/g, ''));
  return Number.isFinite(n) ? n : '';
}

/** Frecuencia de la opción cotizada. Sin dato, se asume mensual. */
export function proposalBillingFrequency(proposal = {}) {
  if (!proposal || typeof proposal !== 'object') return 'Mensual';
  const opciones = Array.isArray(proposal.opciones) ? proposal.opciones : [];
  const active = opciones.find((opt) => opt && opt.isRecommended) || opciones[0];
  return active?.billingFrequency
    || proposal.billingFrequency
    || proposal.anexo?.billingFrequency
    || 'Mensual';
}

/**
 * Valor para el portal de Google: 12 meses de la propuesta.
 * periodAmount es el total del periodo de cobro (mensual, trimestral…), sin multiplicar de nuevo.
 * En GCP, monthlyMxn ya es un mes.
 */
export function annualOpportunityAmount({ proposal = null, periodAmount = 0, billingFrequency = '' } = {}) {
  if (proposal?.kind === 'gcp') {
    const monthly = Number(proposal?.gcp?.monthlyMxn) || Number(periodAmount) || 0;
    return roundMoney(monthly * 12);
  }
  const period = Number(periodAmount) || 0;
  if (!(period > 0)) return 0;
  const months = BILLING_MONTHS[billingFrequency || proposalBillingFrequency(proposal)] || 1;
  return roundMoney(period * (12 / months));
}

/** El correo de la propuesta ya salió. Guardar el borrador no cuenta. */
export function proposalWasSent(proposal) {
  if (!proposal || typeof proposal !== 'object') return false;
  return Boolean(proposal.sentAt || proposal.lastSentAt);
}

/**
 * Workspace o GCP comercial. Capacitación SkillSync no registra oportunidad.
 * El listado del CRM manda la propuesta recortada: basta con hasItems.
 */
export function proposalNeedsGoogleOpportunity(lead = {}, proposal = null) {
  if (!proposal || typeof proposal !== 'object') return false;
  if (proposal.kind === 'skillsync') return false;
  if (proposal.kind === 'gcp') return true;
  if (lead.offerKind === 'capacitacion') return false;
  const hasLicencias = Array.isArray(proposal.licencias) && proposal.licencias.length > 0;
  const hasOpciones = Array.isArray(proposal.opciones) && proposal.opciones.length > 0;
  if (hasLicencias || hasOpciones) return true;
  return proposal.hasItems === true;
}

/** A partir de esta cantidad de usuarios la ejecutiva registra GWS y GE. */
export const GOOGLE_ALTA_MIN_USERS = 6;

const CONSOLE_CHANGE_RE = /cambio\s+de\s+consola/i;

function licenseText(lic) {
  if (lic && typeof lic === 'object') {
    return [lic.sku, lic.skuLabel, lic.description, lic.name, lic.descripcion]
      .filter(Boolean)
      .join(' ');
  }
  return String(lic || '');
}

function licenseQty(lic) {
  return Number(lic?.qty) || Number(lic?.seats) || Number(lic?.cantidad) || 0;
}

/** Gemini Enterprise (Standard, Plus, Business). No incluye Workspace. */
export function isGeminiEnterpriseLine(lic) {
  const n = licenseText(lic).toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
  if (!n.includes('gemini') || !n.includes('enterprise')) return false;
  if (n.includes('google workspace')) return false;
  if (n.includes('workspace business') || n.includes('workspace enterprise')) return false;
  return true;
}

/** Licencias de la opción vigente, o las del expediente si la propuesta no trae líneas. */
export function quotedLicencias(entity = {}) {
  const proposal = entity?.proposal;
  if (proposal && typeof proposal === 'object') {
    const opciones = Array.isArray(proposal.opciones) ? proposal.opciones : [];
    if (opciones.length) {
      const active = activeOptionLicencias(entity);
      if (active.length) return active;
    }
    if (Array.isArray(proposal.licencias) && proposal.licencias.length) return proposal.licencias;
  }
  if (Array.isArray(entity?.licencias) && entity.licencias.length) return entity.licencias;
  return [];
}

/**
 * Personas de la cotización: el mayor entre asientos Workspace y asientos Gemini.
 * null si el listado no trae cantidades.
 */
export function quotedUserCount(entity = {}) {
  const lines = quotedLicencias(entity).filter((lic) => licenseQty(lic) > 0);
  if (lines.length) {
    let gws = 0;
    let ge = 0;
    for (const lic of lines) {
      const qty = licenseQty(lic);
      if (isGeminiEnterpriseLine(lic)) ge += qty;
      else gws += qty;
    }
    const count = Math.max(gws, ge);
    return count > 0 ? count : null;
  }
  const slim = Number(entity?.proposal?.quotedUsers ?? entity?.quotedUsers);
  return Number.isFinite(slim) && slim > 0 ? slim : null;
}

export function isSmallUserQuote(entity = {}) {
  const count = quotedUserCount(entity);
  return count != null && count < GOOGLE_ALTA_MIN_USERS;
}

export function isConsoleChange(entity = {}) {
  const values = [
    entity?.movementType,
    entity?.proposal?.movementType,
    entity?.proposal?.type,
    entity?.proposal?.tipo
  ];
  return values.some((value) => CONSOLE_CHANGE_RE.test(String(value || '')));
}

export function isGcpProposalEntity(entity = {}) {
  return entity?.proposal?.kind === 'gcp';
}

function optionLines(opt) {
  return (Array.isArray(opt?.licencias) ? opt.licencias : []).filter((lic) => licenseQty(lic) > 0);
}

/**
 * La oportunidad comercial: la opción con estrella.
 * Si ninguna está marcada y hay un escenario sin Gemini Enterprise, ese es el que pueden vender.
 * No hereda Gemini de la primera opción del PDF.
 */
function activeOptionLicencias(entity = {}) {
  const opciones = Array.isArray(entity?.proposal?.opciones) ? entity.proposal.opciones : [];
  if (!opciones.length) {
    const proposal = entity?.proposal;
    if (Array.isArray(proposal?.licencias) && proposal.licencias.length) return proposal.licencias;
    if (Array.isArray(entity?.licencias) && entity.licencias.length) return entity.licencias;
    return [];
  }
  const recommended = opciones.find((opt) => opt && opt.isRecommended);
  if (recommended) return Array.isArray(recommended.licencias) ? recommended.licencias : [];
  const withoutGe = opciones.find((opt) => {
    const lines = optionLines(opt);
    return lines.length > 0 && !lines.some((lic) => isGeminiEnterpriseLine(lic));
  });
  if (withoutGe) return withoutGe.licencias;
  const first = opciones.find((opt) => optionLines(opt).length > 0);
  return first?.licencias || [];
}

/**
 * La oportunidad comercial (estrella, o un escenario sin Gemini si nadie la marcó).
 * Si esa opción no trae Gemini Enterprise, solo se pide Workspace y parpadea en rosa.
 */
export function quoteProductFlags(entity = {}) {
  const lines = activeOptionLicencias(entity).filter((lic) => licenseQty(lic) > 0);
  if (lines.length) {
    return {
      known: true,
      gws: lines.some((lic) => !isGeminiEnterpriseLine(lic)),
      ge: lines.some((lic) => isGeminiEnterpriseLine(lic))
    };
  }
  const proposal = entity?.proposal;
  if (proposal && (typeof proposal.hasGe === 'boolean' || typeof proposal.hasGws === 'boolean')) {
    return {
      known: true,
      gws: proposal.hasGws !== false,
      ge: proposal.hasGe === true
    };
  }
  return { known: false, gws: true, ge: false };
}

export function quoteIncludesGe(entity = {}) {
  return quoteProductFlags(entity).ge === true;
}

/** Altas que esta cotización sí pide. Sin GE en la opción vigente, solo Workspace. */
export function requiredGoogleAltaSlots(entity = {}) {
  if (isGcpProposalEntity(entity)) return [];
  const flags = quoteProductFlags(entity);
  const slots = [];
  if (flags.gws) slots.push('gws');
  if (flags.ge) slots.push('ge');
  return slots.length ? slots : ['gws'];
}

/** GWS y GE son dos altas cuando las dos vienen en la opción. GCP sigue siendo una sola. */
export function googleAltaIsDual(entity = {}) {
  return requiredGoogleAltaSlots(entity).length > 1;
}

export function isDualGoogleOpportunity(raw) {
  return Boolean(raw && typeof raw === 'object' && (raw.dual === true || raw.gws || raw.ge));
}

export function normalizeGoogleOpportunity(raw = {}) {
  const amountNum = Number(String(raw.amount ?? '').replace(/,/g, '').replace(/\s/g, ''));
  const amount = Number.isFinite(amountNum) && amountNum > 0
    ? Math.round(amountNum * 100) / 100
    : null;
  const out = {
    required: raw.required === true,
    source: 'Partner',
    partnerId: trim(raw.partnerId),
    googleId: trim(raw.googleId),
    name: trim(raw.name),
    ownerName: trim(raw.ownerName),
    ownerEmail: trim(raw.ownerEmail).toLowerCase(),
    discountExpiresAt: trim(raw.discountExpiresAt).slice(0, 10),
    currency: trim(raw.currency) || 'MXN'
  };
  if (amount != null) out.amount = amount;
  const confirmed = roundMoney(raw.portalAmountConfirmed);
  if (confirmed > 0) out.portalAmountConfirmed = confirmed;
  const quoted = roundMoney(raw.quotedAnnual);
  if (quoted > 0) out.quotedAnnual = quoted;
  out.portalSyncPending = raw.portalSyncPending === true;
  if (raw.portalUpdatedAt) out.portalUpdatedAt = raw.portalUpdatedAt;
  if (trim(raw.portalUpdatedBy)) out.portalUpdatedBy = trim(raw.portalUpdatedBy).toLowerCase();
  if (raw.uploadedAt) out.uploadedAt = raw.uploadedAt;
  if (trim(raw.uploadedBy)) out.uploadedBy = trim(raw.uploadedBy).toLowerCase();
  if (raw.completedAt) out.completedAt = raw.completedAt;
  return out;
}

/** Etiquetas que faltan para apagar el pulso. */
export function googleOpportunityMissing(raw = {}) {
  const g = normalizeGoogleOpportunity(raw);
  const missing = [];
  if (g.partnerId.length < 4) missing.push('ID de oportunidad');
  if (!g.name) missing.push('Nombre de oportunidad');
  if (!g.ownerName) missing.push('Propietario');
  if (!EMAIL_RE.test(g.ownerEmail)) missing.push('Correo del propietario');
  if (!DATE_RE.test(g.discountExpiresAt)) missing.push('Fecha de vencimiento del descuento');
  if (!(g.amount > 0)) missing.push('Valor de oportunidad');
  return missing;
}

export function isGoogleOpportunityComplete(raw) {
  if (!raw) return false;
  return googleOpportunityMissing(raw).length === 0;
}

/**
 * Hay que avisar en el portal solo si el anual de lista ya cambió
 * respecto al que quedó registrado. Un confirmado viejo, distinto del monto
 * guardado, no vuelve a prender el aviso.
 */
export function googleOpportunityPortalPending(raw) {
  if (!raw || typeof raw !== 'object') return false;
  const quoted = roundMoney(raw.quotedAnnual);
  const amount = Number(raw.amount) || 0;
  if (!(quoted > 0) || !(amount > 0)) return false;
  if (isOnlyClientDiscount(quoted, amount)) return false;
  return !amountsClose(amount, quoted);
}

const SLOT_NAME = { gws: 'GWS', ge: 'GE' };
const SLOT_LONG = { gws: 'Google Workspace', ge: 'Gemini Enterprise' };

function slotIsOpen(slot) {
  if (!slot || !isGoogleOpportunityComplete(slot)) return true;
  return googleOpportunityPortalPending(slot);
}

function slotNeedsPortalUpdate(slot) {
  return Boolean(slot && isGoogleOpportunityComplete(slot) && googleOpportunityPortalPending(slot));
}

/** Slots que la cotización pide y todavía no están cerrados. */
export function googleAltaOpenSlots(entity = {}) {
  const required = requiredGoogleAltaSlots(entity);
  const g = entity?.googleOpportunity;
  return required.filter((key) => {
    if (isDualGoogleOpportunity(g)) return slotIsOpen(g[key]);
    if (key !== 'gws') return true;
    if (g && isGoogleOpportunityComplete(g)) return googleOpportunityPortalPending(g);
    return true;
  });
}

export function googleOpportunityChipCopy(entity = {}) {
  const g = entity?.googleOpportunity;
  if (isGcpProposalEntity(entity)) {
    if (g && isGoogleOpportunityComplete(g) && googleOpportunityPortalPending(g)) {
      return {
        label: 'Actualiza Google',
        title: 'La propuesta cambió. Actualiza el valor en el portal de Google.'
      };
    }
    return {
      label: 'Alta Google',
      title: 'Falta registrar el alta de la oportunidad en Google'
    };
  }
  const open = googleAltaOpenSlots(entity);
  const slotOf = (key) => (isDualGoogleOpportunity(g) ? g?.[key] : (key === 'gws' ? g : null));
  if (open.length >= 2) {
    const updates = open.filter((key) => slotNeedsPortalUpdate(slotOf(key)));
    if (updates.length === open.length) {
      return {
        label: 'Actualiza GWS y GE',
        title: 'La propuesta cambió. Actualiza el valor de las dos oportunidades en el portal de Google.'
      };
    }
    return {
      label: 'Faltan GWS y GE',
      title: 'Falta registrar Google Workspace y Gemini Enterprise en el portal de socios.'
    };
  }
  const key = open[0];
  const name = SLOT_NAME[key] || 'Google';
  if (slotNeedsPortalUpdate(slotOf(key))) {
    return {
      label: `Actualiza ${name}`,
      title: `La propuesta cambió. Actualiza ${SLOT_LONG[key] || name} en el portal de Google.`
    };
  }
  return {
    label: `Falta ${name}`,
    title: `Falta registrar la oportunidad de ${SLOT_LONG[key] || name}.`
  };
}

/** La columna Baja comercial ya no pide el alta. */
export function isGoogleAltaBaja(entity = {}) {
  if (!entity || typeof entity !== 'object') return false;
  if (entity.removedFromSalesPipelineReason === 'CRM_BAJA_COMERCIAL') return true;
  const phase = Number(entity.phase ?? entity.crmLeadPhase);
  const status = entity.masterStatus || entity.crmLeadMasterStatus;
  return status === 'BAJA' || phase === 4 || phase === 7;
}

function quoteAsksForGoogleAlta(entity = {}) {
  const g = entity?.googleOpportunity;
  if (g?.required === true || isDualGoogleOpportunity(g)) return true;
  const proposal = entity?.proposal;
  if (proposal?.kind === 'gcp') {
    const monthly = Number(proposal?.gcp?.monthlyMxn) || Number(proposal?.gcp?.monthlyUsd) || 0;
    return monthly > 0;
  }
  if (!proposalWasSent(proposal)) return false;
  return proposalNeedsGoogleOpportunity(entity, proposal);
}

/**
 * Pulso hasta registrar el alta, o hasta avisar que el valor nuevo ya está en Google.
 * Workspace y Gemini Enterprise son dos altas. 5 usuarios o menos, y el cambio de consola, no piden registro.
 * En Baja comercial el pulso se apaga.
 */
export function googleOpportunityNeedsAlta(entity = {}) {
  if (isGoogleAltaBaja(entity)) return false;
  if (isConsoleChange(entity)) return false;
  if (!isGcpProposalEntity(entity) && isSmallUserQuote(entity)) return false;
  if (!quoteAsksForGoogleAlta(entity)) return false;
  if (isGcpProposalEntity(entity)) {
    const g = entity?.googleOpportunity;
    if (g && googleOpportunityPortalPending(g) && isGoogleOpportunityComplete(g)) return true;
    if (g && isGoogleOpportunityComplete(g)) return false;
    return true;
  }
  return googleAltaOpenSlots(entity).length > 0;
}

/**
 * 'pink' si falta GWS (aunque la cotización no traiga Gemini).
 * 'blue' si solo falta GE. 'split' si faltan las dos.
 */
export function googleOpportunityAltaTone(entity = {}) {
  if (!googleOpportunityNeedsAlta(entity)) return null;
  if (isGcpProposalEntity(entity)) return 'pink';
  const open = googleAltaOpenSlots(entity);
  const ge = open.includes('ge');
  const gws = open.includes('gws');
  if (ge && gws) return 'split';
  if (ge) return 'blue';
  if (gws) return 'pink';
  return 'pink';
}

export function googleAltaCardClass(entity = {}) {
  const tone = googleOpportunityAltaTone(entity);
  if (tone === 'blue') return ' needs-google-alta is-alta-ge';
  if (tone === 'split') return ' needs-google-alta is-alta-both';
  if (tone === 'pink') return ' needs-google-alta is-alta-gws';
  return '';
}

export function googleAltaPulseTabClass(tone) {
  if (tone === 'blue') return ' is-pulse is-pulse-blue';
  if (tone === 'split') return ' is-pulse is-pulse-split';
  if (tone === 'pink') return ' is-pulse is-pulse-pink';
  return '';
}

export function googleAltaHasRecord(raw) {
  if (!raw || typeof raw !== 'object') return false;
  if (isDualGoogleOpportunity(raw)) {
    return ['gws', 'ge'].some((key) => {
      const slot = raw[key];
      return Boolean(slot && (slot.uploadedAt || slot.partnerId || slot.required === true || isGoogleOpportunityComplete(slot)));
    });
  }
  return Boolean(raw.required || raw.uploadedAt || isGoogleOpportunityComplete(raw));
}

export function shouldShowGoogleAlta(entity = {}) {
  if (isConsoleChange(entity)) {
    return quoteAsksForGoogleAlta(entity) || googleAltaHasRecord(entity?.googleOpportunity);
  }
  if (googleOpportunityNeedsAlta(entity)) return true;
  return googleAltaHasRecord(entity?.googleOpportunity);
}

/** El envío de la propuesta pide el alta, salvo cotización chica o cambio de consola. */
export function googleAltaBlocksSend(entity = {}) {
  if (isGoogleAltaBaja(entity)) return false;
  if (isConsoleChange(entity)) return false;
  if (!proposalNeedsGoogleOpportunity(entity, entity?.proposal)) return false;
  if (!isGcpProposalEntity(entity) && isSmallUserQuote(entity)) return false;
  return true;
}

export function googleAltaAlreadyUploaded(raw, entity) {
  if (!raw || typeof raw !== 'object') return false;
  const slots = entity ? requiredGoogleAltaSlots(entity) : ['gws', 'ge'];
  if (isDualGoogleOpportunity(raw)) {
    return slots.every((key) => Boolean(raw[key]?.uploadedAt));
  }
  if (slots.length === 1 && slots[0] === 'ge') return Boolean(raw.uploadedAt);
  return Boolean(raw.uploadedAt || raw.gws?.uploadedAt);
}

function quotedPriceType(entity = {}) {
  const opciones = Array.isArray(entity?.proposal?.opciones) ? entity.proposal.opciones : [];
  const recommended = opciones.find((opt) => opt && opt.isRecommended);
  const opt = recommended || opciones.find((item) => optionLines(item).length) || opciones[0];
  const label = opt?.vigenciaLabel
    || entity?.proposal?.vigenciaLabel
    || entity?.proposal?.anexo?.vigenciaLabel
    || '';
  return catalogPriceTypeFromLabel(label);
}

function lineListUnit(lic, priceList, priceType) {
  const onLine = Number(lic?.precioUnitario ?? lic?.precio ?? lic?.unitPrice) || 0;
  if (onLine > 0) return onLine;
  if (!priceList || typeof priceList !== 'object') return 0;
  return catalogMonthlyUnit(lic, priceList, {}, priceType) || 0;
}

/**
 * Anual de lista para el portal. No resta el descuento al cliente
 * ni usa el total cobrado. Sin precio de línea ni de catálogo, regresa 0.
 */
export function suggestedAmountForSlot(entity = {}, slot = 'gws', fallbackAnnual = 0, priceList = null) {
  const lines = quotedLicencias(entity).filter((lic) => licenseQty(lic) > 0);
  const mine = lines.filter((lic) => (slot === 'ge') === isGeminiEnterpriseLine(lic));
  const priceType = quotedPriceType(entity);
  const priced = mine.reduce((sum, lic) => {
    const unit = lineListUnit(lic, priceList, priceType);
    if (!(unit > 0)) return sum;
    return sum + unit * licenseQty(lic);
  }, 0);
  if (priced > 0) {
    return annualOpportunityAmount({ proposal: entity.proposal, periodAmount: priced });
  }
  if (lines.length) return 0;
  const fallback = roundMoney(fallbackAnnual);
  if (slot === 'gws' && fallback > 0 && isGcpProposalEntity(entity)) return fallback;
  return 0;
}

/** Anuales de lista de Workspace y Gemini. total 0 si no hay precio. */
export function listOpportunityAmounts(entity = {}, priceList = null) {
  const gws = suggestedAmountForSlot(entity, 'gws', 0, priceList);
  const ge = suggestedAmountForSlot(entity, 'ge', 0, priceList);
  const total = Math.round((((gws || 0) + (ge || 0)) * 100)) / 100;
  return { gws, ge, total };
}

/**
 * Anual de lista del escenario que se acaba de guardar.
 * La estrella de otro escenario no cuenta: si esa pestaña sigue en 35,
 * no puede dejar el alta en 35 cuando aquí ya hay 40.
 */
export function listOpportunityAmountsForOption(option, proposal = {}, priceList = null) {
  if (!option || typeof option !== 'object') return { gws: 0, ge: 0, total: 0 };
  return listOpportunityAmounts({
    proposal: {
      ...(proposal && typeof proposal === 'object' ? proposal : {}),
      opciones: [{ ...option, isRecommended: true }]
    }
  }, priceList);
}

/**
 * Actualiza el monto de un alta ya completa.
 * Si el número nuevo es el mismo que ya confirmó en el portal, no deja el aviso prendido.
 */
function keepListAmount(slot, listAmount) {
  const list = roundMoney(listAmount);
  if (!(list > 0)) return slot;
  return normalizeGoogleOpportunity({
    ...slot,
    required: true,
    amount: list,
    quotedAnnual: list,
    portalAmountConfirmed: list,
    portalSyncPending: false
  });
}

function refreshSlotAmount(slot, nextAmount) {
  if (!slot || typeof slot !== 'object') return slot;
  if (!isGoogleOpportunityComplete(slot)) return slot;
  const next = roundMoney(nextAmount);
  const stored = Number(slot.amount) || 0;
  const acknowledged = roundMoney(slot.quotedAnnual) || roundMoney(stored);
  if (isOnlyClientDiscount(acknowledged, next) || isOnlyClientDiscount(acknowledged, stored)) {
    return keepListAmount(slot, acknowledged);
  }
  if (!(next > 0)) return slot;
  if (amountsClose(stored, next)) {
    if (slot.portalSyncPending === true && !amountsClose(acknowledged, stored)) return slot;
    const confirmed = Number(slot.portalAmountConfirmed) || 0;
    const stale = slot.portalSyncPending === true
      || (confirmed > 0 && !amountsClose(stored, confirmed))
      || !amountsClose(slot.quotedAnnual, next);
    if (!stale) return slot;
    return normalizeGoogleOpportunity({
      ...slot,
      required: true,
      amount: stored,
      quotedAnnual: stored,
      portalAmountConfirmed: stored,
      portalSyncPending: false
    });
  }
  const portalValue = slot.portalSyncPending === true && stored > 0
    ? roundMoney(stored)
    : acknowledged;
  return normalizeGoogleOpportunity({
    ...slot,
    required: true,
    amount: next,
    quotedAnnual: portalValue,
    portalAmountConfirmed: portalValue,
    portalSyncPending: !amountsClose(next, portalValue)
  });
}

/**
 * Si el alta ya estaba completa y la propuesta cambia de monto, guarda el anual nuevo
 * y deja el aviso pendiente hasta que el ejecutivo confirme el portal.
 * En las dos altas, solo se marca la oportunidad cuyo propio valor cambió.
 * slotAmounts: { gws, ge } con el anual de cada producto. Sin ese dato no se marcan.
 */
function slotListTotal(slotAmounts) {
  if (!slotAmounts || typeof slotAmounts !== 'object') return 0;
  const gws = roundMoney(slotAmounts.gws);
  const ge = roundMoney(slotAmounts.ge);
  return Math.round(((gws || 0) + (ge || 0)) * 100) / 100;
}

export function refreshGoogleOpportunityFromProposal(existing, nextAmount, slotAmounts = null) {
  const slotsGiven = slotAmounts && typeof slotAmounts === 'object';
  const listTotal = slotListTotal(slotAmounts);
  if (isDualGoogleOpportunity(existing)) {
    const slots = slotsGiven ? slotAmounts : {};
    const gws = refreshSlotAmount(existing.gws, slots.gws);
    const ge = refreshSlotAmount(existing.ge, slots.ge);
    const next = listTotal > 0 ? listTotal : roundMoney(nextAmount);
    const prev = roundMoney(existing.proposalAnnual);
    const annualChanged = next > 0 && (!(prev > 0) || !amountsClose(prev, next));
    const slotsChanged = gws !== existing.gws || ge !== existing.ge;
    if (!annualChanged && !slotsChanged) return null;
    return {
      ...existing,
      required: true,
      dual: true,
      ...(next > 0 ? { proposalAnnual: next } : {}),
      gws,
      ge
    };
  }
  if (!isGoogleOpportunityComplete(existing)) return null;
  const stored = Number(existing.amount) || 0;
  const list = roundMoney(existing.quotedAnnual) || stored;
  if (slotsGiven && !(listTotal > 0)) {
    if (isOnlyClientDiscount(list, stored)) return keepListAmount(existing, list);
    return null;
  }
  const next = listTotal > 0 ? listTotal : roundMoney(nextAmount);
  if (!(next > 0)) return null;
  if (isOnlyClientDiscount(list, next)) {
    return keepListAmount(existing, list);
  }
  if (amountsClose(stored, next)) {
    const confirmed = Number(existing.portalAmountConfirmed) || 0;
    const stale = existing.portalSyncPending === true
      || (confirmed > 0 && !amountsClose(stored, confirmed))
      || (roundMoney(existing.quotedAnnual) > 0 && !amountsClose(existing.quotedAnnual, stored));
    if (!stale) return null;
    if (roundMoney(existing.quotedAnnual) > 0 && !amountsClose(existing.quotedAnnual, stored)) return null;
    return normalizeGoogleOpportunity({
      ...existing,
      required: true,
      amount: stored,
      quotedAnnual: stored,
      portalAmountConfirmed: stored,
      portalSyncPending: false
    });
  }
  const confirmedRaw = Number(existing.portalAmountConfirmed);
  const confirmed = existing.portalSyncPending === true && stored > 0
    ? roundMoney(stored)
    : (Number.isFinite(confirmedRaw) && confirmedRaw > 0
      ? roundMoney(confirmedRaw)
      : (roundMoney(existing.quotedAnnual) || roundMoney(stored)));
  return normalizeGoogleOpportunity({
    ...existing,
    required: true,
    amount: next,
    quotedAnnual: confirmed,
    portalAmountConfirmed: confirmed,
    portalSyncPending: !amountsClose(next, confirmed)
  });
}

/** El ejecutivo avisó que el valor ya quedó en el portal de Google. */
export function confirmGooglePortalAmount(existing, { by } = {}) {
  const now = new Date().toISOString();
  const amount = roundMoney(existing?.amount);
  return normalizeGoogleOpportunity({
    ...(existing || {}),
    required: true,
    amount: amount || existing?.amount,
    quotedAnnual: amount || existing?.amount,
    portalAmountConfirmed: amount || existing?.amount,
    portalSyncPending: false,
    portalUpdatedAt: now,
    portalUpdatedBy: by || existing?.portalUpdatedBy || ''
  });
}

function markGoogleAltaSlot(existing, { by, amount, currency } = {}) {
  const now = new Date().toISOString();
  const base = normalizeGoogleOpportunity(existing || {});
  const suggested = Number(amount);
  return normalizeGoogleOpportunity({
    ...base,
    required: true,
    source: 'Partner',
    uploadedAt: base.uploadedAt || now,
    uploadedBy: base.uploadedBy || by || '',
    amount: base.amount > 0 ? base.amount : (Number.isFinite(suggested) && suggested > 0 ? suggested : null),
    currency: base.currency || currency || 'MXN'
  });
}

export function markGoogleOpportunityUploaded(existing, { by, amount, currency, dual = false, slots } = {}) {
  if (dual) {
    const prev = existing && typeof existing === 'object' ? existing : {};
    const seedGws = prev.gws || (!isDualGoogleOpportunity(prev) ? prev : null);
    const annual = roundMoney(amount);
    const keys = Array.isArray(slots) && slots.length ? slots : ['gws', 'ge'];
    const next = {
      required: true,
      dual: true,
      source: 'Partner',
      ...(annual > 0 ? { proposalAnnual: annual } : {})
    };
    if (keys.includes('gws')) next.gws = markGoogleAltaSlot(seedGws, { by, currency });
    else if (prev.gws) next.gws = prev.gws;
    if (keys.includes('ge')) next.ge = markGoogleAltaSlot(prev.ge, { by, currency });
    else if (prev.ge) next.ge = prev.ge;
    return next;
  }
  return markGoogleAltaSlot(existing, { by, amount, currency });
}

export function confirmGoogleAltaSlot(existing, slotKey, meta) {
  if (!isDualGoogleOpportunity(existing) || (slotKey !== 'gws' && slotKey !== 'ge')) {
    return confirmGooglePortalAmount(existing, meta);
  }
  return {
    ...existing,
    required: true,
    dual: true,
    [slotKey]: confirmGooglePortalAmount(existing[slotKey], meta)
  };
}

export function mergeGoogleAltaSlot(existing, slotKey, slot) {
  const prev = existing && typeof existing === 'object' ? existing : {};
  const next = {
    required: true,
    dual: true,
    source: 'Partner'
  };
  if (prev.proposalAnnual) next.proposalAnnual = prev.proposalAnnual;
  if (isDualGoogleOpportunity(prev)) {
    if (prev.gws) next.gws = prev.gws;
    if (prev.ge) next.ge = prev.ge;
  } else if (prev.partnerId || prev.uploadedAt || prev.name) {
    next.gws = normalizeGoogleOpportunity(prev);
  }
  next[slotKey] = slot;
  return next;
}

export function applyGoogleOpportunityDraft(existing, draft = {}) {
  const wasComplete = isGoogleOpportunityComplete(existing);
  const quotedExisting = roundMoney(existing?.quotedAnnual);
  const confirmedRaw = Number(existing?.portalAmountConfirmed);
  const confirmed = Number.isFinite(confirmedRaw) && confirmedRaw > 0 ? roundMoney(confirmedRaw) : 0;
  const merged = normalizeGoogleOpportunity({
    ...(existing || {}),
    ...draft,
    required: true,
    source: 'Partner',
    completedAt: undefined,
    quotedAnnual: quotedExisting > 0 ? quotedExisting : undefined,
    portalAmountConfirmed: confirmed > 0 ? confirmed : undefined,
    portalSyncPending: existing?.portalSyncPending === true
  });
  if (isGoogleOpportunityComplete(merged)) {
    merged.completedAt = existing?.completedAt || new Date().toISOString();
    const acknowledged = roundMoney(merged.quotedAnnual);
    if (wasComplete && acknowledged > 0 && !amountsClose(merged.amount, acknowledged)) {
      merged.quotedAnnual = acknowledged;
      merged.portalAmountConfirmed = acknowledged;
      merged.portalSyncPending = true;
    } else {
      merged.quotedAnnual = merged.amount;
      merged.portalAmountConfirmed = merged.amount;
      merged.portalSyncPending = false;
    }
  } else if (quotedExisting > 0) {
    merged.quotedAnnual = quotedExisting;
    merged.portalSyncPending = false;
  }
  return merged;
}

function preferGoogleAltaSlot(primary, secondary) {
  const left = primary && typeof primary === 'object' ? primary : null;
  const right = secondary && typeof secondary === 'object' ? secondary : null;
  if (!left && !right) return null;
  if (!left) return normalizeGoogleOpportunity(right);
  if (!right) return normalizeGoogleOpportunity(left);
  const leftMissing = googleOpportunityMissing(left).length;
  const rightMissing = googleOpportunityMissing(right).length;
  if (leftMissing !== rightMissing) {
    return normalizeGoogleOpportunity(leftMissing < rightMissing ? left : right);
  }
  const leftPending = googleOpportunityPortalPending(left);
  const rightPending = googleOpportunityPortalPending(right);
  if (leftPending !== rightPending) {
    return normalizeGoogleOpportunity(leftPending ? left : right);
  }
  return normalizeGoogleOpportunity(left);
}

/** Conserva el registro con más datos. Empate: gana el que todavía debe avisarse en Google. */
export function preferGoogleOpportunity(primary, secondary) {
  const left = primary && typeof primary === 'object' ? primary : null;
  const right = secondary && typeof secondary === 'object' ? secondary : null;
  if (!left && !right) return null;
  if (isDualGoogleOpportunity(left) || isDualGoogleOpportunity(right)) {
    const gws = preferGoogleAltaSlot(
      left?.gws || (!isDualGoogleOpportunity(left) ? left : null),
      right?.gws || (!isDualGoogleOpportunity(right) ? right : null)
    );
    const ge = preferGoogleAltaSlot(
      isDualGoogleOpportunity(left) ? left?.ge : null,
      isDualGoogleOpportunity(right) ? right?.ge : null
    );
    const annual = roundMoney(left?.proposalAnnual) || roundMoney(right?.proposalAnnual);
    return {
      required: true,
      dual: true,
      source: 'Partner',
      ...(annual > 0 ? { proposalAnnual: annual } : {}),
      ...(gws ? { gws } : {}),
      ...(ge ? { ge } : {})
    };
  }
  return preferGoogleAltaSlot(left, right);
}
